import { Group, Stack, Text } from '@mantine/core';
import type { Spending } from '../../../api/financials-client/financials-client.type';
import { formatCurrency } from '../../../helpers/financial/financial';
import { addOpacityToColor } from '../../../helpers/string-helpers/string-helpers';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import {
  blueLightest,
  primaryLightest,
  purpleLightest,
  redLightest,
  skyDark,
  skyLight,
  yellowLightest,
} from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import {
  getAverageMonthlySpendingPerProject,
  getMaximumMonthlySpend,
  getMedianMonthlySpendingPerProject,
  getMinimumMonthlySpend,
  getTotalSpending,
} from './helpers';

function Overview({ spendingData }: { spendingData: Spending[] | undefined }) {
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);

  const totalBudget = getTotalSpending(spendingData, selectedYear);
  const averageMonthly = getAverageMonthlySpendingPerProject(spendingData, selectedYear);
  const medianMonthly = getMedianMonthlySpendingPerProject(spendingData, selectedYear);
  const minimumMonthly = getMinimumMonthlySpend(spendingData, selectedYear);
  const maximumMonthly = getMaximumMonthlySpend(spendingData, selectedYear);

  return (
    <Group gap={16}>
      <OverviewCard
        dataPoint={totalBudget !== null ? formatCurrency(totalBudget) : '--'}
        description="All projects combined"
        color={purpleLightest}
        iconName="payments"
        label="Total Budget"
      />
      <OverviewCard
        dataPoint={averageMonthly !== null ? formatCurrency(averageMonthly) : '--'}
        description="Per project average"
        color={primaryLightest}
        iconName="calendar_month"
        label="Average Monthly"
      />
      <OverviewCard
        dataPoint={minimumMonthly !== null ? formatCurrency(minimumMonthly) : '--'}
        description="Minimum monthly spending"
        color={blueLightest}
        iconName="south_east"
        label="Minimum Spend"
      />
      <OverviewCard
        dataPoint={medianMonthly !== null ? formatCurrency(medianMonthly) : '--'}
        description="Per project median"
        color={redLightest}
        iconName="arrow_upward"
        label="Median Monthly"
      />
      <OverviewCard
        dataPoint={maximumMonthly !== null ? formatCurrency(maximumMonthly) : '--'}
        description="Peak monthly spending"
        color={yellowLightest}
        iconName="north_east"
        label="Highest Spend"
      />
    </Group>
  );
}

function OverviewCard({
  dataPoint,
  description,
  color,
  iconName,
  label,
}: {
  dataPoint: string;
  description: string;
  color: string;
  iconName: string;
  label: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        backgroundColor: addOpacityToColor(color, 0.25),
        borderRadius: 10,
        border: `2px solid ${skyLight}`,
        height: 140,
      }}
    >
      <Stack p={10}>
        <Group gap={2}>
          <Icon name={iconName} size={24} color={skyDark} />
          <Text fw={600}>{label}</Text>
        </Group>
        <Text fw={700} size="xl" ta="center">
          {dataPoint}
        </Text>
        <Text size="sm" fw={300} c={skyDark} ta="center">
          {description}
        </Text>
      </Stack>
    </div>
  );
}

export { Overview };
